import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ChevronRight,
  Upload,
  Plus,
  Trash2,
  ChevronDown,
  Info,
  Link as LinkIcon,
  List,
  X,
  User,
  Phone,
  Mail,
  MapPin,
  Building2,
  FileText,
  Calendar,
  Loader2,
  Save,
  CheckCircle2,
  Check
} from 'lucide-react';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import { useLoading } from '../../context/LoadingContext';
import Header from '../../components/Dashboard/Header/Header';
import styles from './CreateLOI.module.css'; // Reusing the same styles
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import VendorDrawer from '../../components/Vendors/VendorDrawer';
import CustomDatePicker from '../../components/Common/DatePicker/CustomDatePicker';
import CustomSelect from '../../components/Common/Select/CustomSelect';
import api, { getStorageUrl } from '../../api/axios';
import toast from 'react-hot-toast';
import NotFound from '../Error/NotFound';

const EditLOI = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { loading, showLoader, hideLoader } = useLoading();
  const pathPrefix = '/projects/lois';
  const labelBase = 'LOI';
  const labelFull = 'LOI (Letter of Intent)';
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isAddVendorOpen, setIsAddVendorOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Dropdown data
  const [projectsData, setProjectsData] = useState([]);
  const [vendorsData, setVendorsData] = useState([]);
  const [currenciesData, setCurrenciesData] = useState([]);
  const [taxesData, setTaxesData] = useState([]);
  const [workOrdersData, setWorkOrdersData] = useState([]);
  const [fullItemsData, setFullItemsData] = useState([]);
  const [fetchingDropdowns, setFetchingDropdowns] = useState(false);
  const [fetchingWorkOrders, setFetchingWorkOrders] = useState(false);
  const [formData, setFormData] = useState({
    project_id: '',
    vendor_id: '',
    loi_number: '',
    loi_date: '',
    expected_delivery_date: '',
    currency_id: '',
    payment_terms: '',
    internal_notes: '',
    vendor_instructions: '',
    round_off: '',
  });

  const [poDocument, setPoDocument] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPoDocument(file);
      if (file.type.startsWith('image/')) {
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
      } else {
        setPreviewUrl(null);
      }
    }
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file) {
      setPoDocument(file);
      if (file.type.startsWith('image/')) {
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
      } else {
        setPreviewUrl(null);
      }
    }
  };

  const [selectedWorkOrders, setSelectedWorkOrders] = useState([]);
  const [showWorkOrderDropdown, setShowWorkOrderDropdown] = useState(false);
  const [orderItems, setOrderItems] = useState([]);

  useEffect(() => {
    fetchDropdowns();
  }, []);

  const fetchDropdowns = async () => {
    setFetchingDropdowns(true);
    try {
      const [projRes, vendRes, currRes, taxRes] = await Promise.all([
        api.get('/projects-dropdown', { skipLoader: true }),
        api.get('/vendors-dropdown', { skipLoader: true }),
        api.get('/currencies-dropdown', { skipLoader: true }),
        api.get('/taxes-dropdown', { skipLoader: true })
      ]);

      if (projRes.data.success) setProjectsData(projRes.data.data);
      if (vendRes.data.success) setVendorsData(vendRes.data.data);
      if (currRes.data.success) setCurrenciesData(currRes.data.data);
      if (taxRes.data.success) setTaxesData(taxRes.data.data);
    } catch (error) {
      console.error('Error fetching dropdowns:', error);
      toast.error('Failed to load form data');
    } finally {
      setFetchingDropdowns(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchLOI(id);
    }
  }, [id]);

  const fetchLOI = async (poId) => {
    try {
      const response = await api.get(`/letter-of-intents/${poId}`);
      if (response.data.success) {
        const po = response.data.data;
        setFormData({
          project_id: po.project_id || '',
          vendor_id: po.vendor_id || '',
          loi_number: po.loi_number || '',
          loi_date: po.loi_date ? new Date(po.loi_date).toLocaleDateString('en-CA') : '',
          expected_delivery_date: po.expected_delivery_date ? new Date(po.expected_delivery_date).toLocaleDateString('en-CA') : '',
          currency_id: po.currency_id || '',
          payment_terms: po.payment_terms || '',
          internal_notes: po.internal_notes || '',
          vendor_instructions: po.vendor_instructions || '',
          round_off: po.round_off || ''
        });

        if (po.items && po.items.length > 0) {
          const mappedItems = po.items.map(item => {
            const qty = parseFloat(item.quantity) || 0;
            const rate = parseFloat(item.rate) || 0;
            const subtotal = qty * rate;
            const taxPercentage = item.tax?.tax_percentage ? parseFloat(item.tax.tax_percentage) : 0;
            const taxAmount = (subtotal * taxPercentage) / 100;

            return {
              id: item.id,
              work_order_item_id: item.work_order_item_id,
              name: item.item_name || '',
              qty: qty,
              unit: item.unit?.unit_name || 'Nos',
              unit_id: item.unit_id,
              rate: rate,
              tax_id: item.tax_id || '',
              tax_percentage: taxPercentage,
              tax_amount: taxAmount,
              amount: subtotal + taxAmount
            };
          });
          setOrderItems(mappedItems);
        }

        if (po.loi_document_filepath) {
          const fullPath = getStorageUrl(po.loi_document_filepath);
          setPoDocument({ name: po.loi_document_filename });
          setPreviewUrl(fullPath);
        }
      }
    } catch (error) {
      console.error(`Error fetching ${labelBase}:`, error);
      toast.error(`Failed to fetch ${labelBase} details`);
    }
  };

  useEffect(() => {
    if (formData.project_id) {
      fetchWorkOrders(formData.project_id);
    } else {
      setWorkOrdersData([]);
      setSelectedWorkOrders([]);
    }
  }, [formData.project_id]);

  const fetchWorkOrders = async (projectId) => {
    setFetchingWorkOrders(true);
    try {
      const dropdownRes = await api.get(`/work-order-items-dropdown?project_id=${projectId}`, { skipLoader: true });
      if (dropdownRes.data.success) {
        setWorkOrdersData(dropdownRes.data.data);
      }
      const detailsRes = await api.get(`/work-order-items?project_id=${projectId}&per_page=100`, { skipLoader: true });
      if (detailsRes.data.success) {
        setFullItemsData(detailsRes.data.data.data);
      }
    } catch (error) {
      console.error('Error fetching work orders:', error);
    } finally {
      setFetchingWorkOrders(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (name, date) => {
    setFormData(prev => ({
      ...prev,
      [name]: date ? date.toLocaleDateString('en-CA') : ''
    }));
  };

  const handleItemChange = (id, field, value) => {
    setOrderItems(prev => prev.map(item => {
      if (item.id === id) {
        const updatedItem = { ...item, [field]: value };

        // Recalculate if qty, rate, or tax changes
        if (['qty', 'rate', 'tax_id'].includes(field)) {
          const qty = field === 'qty' ? value : item.qty;
          const rate = field === 'rate' ? value : item.rate;
          const subtotal = qty * rate;

          let taxPercentage = item.tax_percentage || 0;
          if (field === 'tax_id') {
            const selectedTax = taxesData.find(t => t.id === value);
            taxPercentage = selectedTax ? parseFloat(selectedTax.tax_percentage) : 0;
            updatedItem.tax_id = value;
          }

          const taxAmount = (subtotal * taxPercentage) / 100;
          updatedItem.tax_percentage = taxPercentage;
          updatedItem.tax_amount = taxAmount;
          updatedItem.amount = subtotal + taxAmount;
        }
        return updatedItem;
      }
      return item;
    }));
  };

  const calculateTotals = () => {
    const subtotal = orderItems.reduce((acc, item) => acc + (item.qty * item.rate), 0);
    const taxAmount = orderItems.reduce((acc, item) => acc + (item.tax_amount || 0), 0);
    const roundOff = parseFloat(formData.round_off) || 0;
    const total = subtotal + taxAmount + roundOff;

    return {
      subtotal,
      taxAmount,
      roundOff,
      total
    };
  };

  const totals = calculateTotals();

  const handleLoadItems = () => {
    if (selectedWorkOrders.length === 0) {
      toast.error('Please select at least one work order');
      return;
    }
    const selectedData = fullItemsData.filter(item => selectedWorkOrders.includes(item.id));
    const newItems = selectedData.map(item => {
      const defaultTax = taxesData[0] || null;
      const rate = parseFloat(item.sales_price) || 0;
      const qty = parseFloat(item.quantity) || 0;
      const subtotal = rate * qty;
      const taxPercentage = defaultTax ? parseFloat(defaultTax.tax_percentage) : 0;
      const taxAmount = (subtotal * taxPercentage) / 100;

      return {
        id: `new-${Date.now()}-${item.id}`, // Unique UI ID to avoid collision with PKs
        work_order_item_id: item.id,
        name: item.item_name,
        qty: qty,
        unit: item.unit?.unit_name || item.unit_name || 'Nos',
        unit_id: item.unit_id,
        rate: rate,
        tax_id: defaultTax ? defaultTax.id : '',
        tax_percentage: taxPercentage,
        tax_amount: taxAmount,
        amount: subtotal + taxAmount
      };
    });
    setOrderItems(prev => {
      const existingWOIds = prev.map(i => i.work_order_item_id);
      const uniqueNewItems = newItems.filter(i => !existingWOIds.includes(i.work_order_item_id));
      return [...prev, ...uniqueNewItems];
    });
    setSelectedWorkOrders([]);
    setShowWorkOrderDropdown(false);
    toast.success(`${newItems.length} items loaded`);
  };

  const removeOrderItem = (id) => {
    setOrderItems(prev => prev.filter(item => item.id !== id));
  };

  const handleUpdate = async (statusOverride = null) => {
    const { subtotal, taxAmount, roundOff, total } = calculateTotals();
    setIsSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('_method', 'PUT');
      fd.append('project_id', formData.project_id);
      fd.append('vendor_id', formData.vendor_id);
      fd.append('loi_number', formData.loi_number);
      if (statusOverride) fd.append('status', statusOverride);
      fd.append('loi_date', formData.loi_date);
      fd.append('expected_delivery_date', formData.expected_delivery_date);
      fd.append('currency_id', formData.currency_id);
      fd.append('payment_terms', formData.payment_terms || '');
      fd.append('internal_notes', formData.internal_notes || '');
      fd.append('vendor_instructions', formData.vendor_instructions || '');
      fd.append('subtotal_amount', subtotal);
      fd.append('total_tax_amount', taxAmount);
      fd.append('round_off', roundOff);
      fd.append('total_amount', total);

      if (poDocument && poDocument instanceof File) {
        fd.append('loi_document', poDocument);
      }

      orderItems.forEach((item, index) => {
        const itemSubtotal = item.qty * item.rate;
        const itemTaxAmount = item.tax_amount || 0;

        // If it's an existing item, pass its PK ID
        if (item.id && typeof item.id === 'number') {
          fd.append(`items[${index}][id]`, item.id);
        }

        fd.append(`items[${index}][work_order_item_id]`, item.work_order_item_id);
        fd.append(`items[${index}][item_name]`, item.name);
        fd.append(`items[${index}][quantity]`, item.qty);
        fd.append(`items[${index}][rate]`, item.rate);
        fd.append(`items[${index}][unit_id]`, item.unit_id);
        fd.append(`items[${index}][tax_id]`, item.tax_id);
        fd.append(`items[${index}][subtotal_amount]`, itemSubtotal);
        fd.append(`items[${index}][tax_amount]`, itemTaxAmount);
        fd.append(`items[${index}][total_amount]`, itemSubtotal + itemTaxAmount);
      });

      const response = await api.post(`/letter-of-intents/${id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        toast.success(`${labelFull} updated successfully`);
        navigate(pathPrefix);
      }
    } catch (error) {
      console.error(`Error updating ${labelBase}:`, error);
      toast.error(error.response?.data?.message || `Failed to update ${labelFull}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  if (loading) {
    return (
      <div className={`${styles['create-po-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
        {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <div className={styles['content-wrapper']}>
          <Header onMenuClick={toggleSidebar} />
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: '20px' }}>
            <Loader2 size={48} className={styles['spinner']} color="#f59e0b" />
            <p style={{ color: '#64748b', fontWeight: 600 }}>Loading LOI details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!formData.loi_number && !loading) {
    return <NotFound />;
  }

  return (
    <div className={`${styles['create-po-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={toggleSidebar} />

        <main className={styles['main-content']}>
          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <Breadcrumbs />
              <h1>Update {labelFull}</h1>
              <p>Modify and manage details for this {labelBase}.</p>
            </div>
          </div>

          <div className={styles['form-sections-grid']}>
            {/* Left Section: Basic Info */}
            <div className={[styles['form-card'], styles['basic-info-card']].join(' ')}>
              <div className={styles['card-header-po']}>
                <Info size={18} className={styles['text-orange']} />
                <h3>Basic Information</h3>
              </div>
              <div className={styles['card-content']}>
                <div className={styles['form-grid-2']}>
                  <div className={styles['form-group']}>
                    <label>Project <span className={styles['required-star']}>*</span></label>
                    <CustomSelect
                      value={formData.project_id ? (() => {
                        const p = projectsData.find(project => project.id === formData.project_id);
                        return p ? { value: p.id, label: `${p.project_name} (${p.project_code})` } : null;
                      })() : null}
                      onChange={(opt) => setFormData(prev => ({ ...prev, project_id: opt ? opt.value : '' }))}
                      options={projectsData.map(p => ({ value: p.id, label: `${p.project_name} (${p.project_code})` }))}
                      placeholder="Select Project"
                      isLoading={fetchingDropdowns}
                    />
                  </div>

                  <div className={styles['flex-row']}>
                    <div className={[styles['form-group'], styles['flex-1']].join(' ')}>
                      <label>Vendor <span className={styles['required-star']}>*</span></label>
                      <CustomSelect
                        value={formData.vendor_id ? {
                          value: formData.vendor_id,
                          label: vendorsData.find(v => v.id === formData.vendor_id)?.vendor_name
                        } : null}
                        onChange={(opt) => setFormData(prev => ({ ...prev, vendor_id: opt ? opt.value : '' }))}
                        options={vendorsData.map(v => ({ value: v.id, label: v.vendor_name }))}
                        placeholder="Select Vendor"
                        isLoading={fetchingDropdowns}
                      />
                    </div>
                    <button type="button" className={styles['btn-add-vendor']} onClick={() => setIsAddVendorOpen(true)}>
                      <Plus size={18} />
                    </button>
                  </div>
                </div>

                <div className={styles['form-grid-2']}>
                  <div className={styles['form-group']}>
                    <label>{labelBase} Number <span className={styles['required-star']}>*</span></label>
                    <input
                      type="text"
                      name="loi_number"
                      value={formData.loi_number}
                      placeholder="e.g. LOI-2024-001"
                      onChange={handleInputChange}
                      className={styles['disabled-input']}
                      disabled
                    />
                  </div>
                  <div className={styles['form-group']}>
                    <label>{labelBase} Date <span className={styles['required-star']}>*</span></label>
                    <CustomDatePicker
                      selected={formData.loi_date}
                      onChange={(date) => handleDateChange('loi_date', date)}
                      placeholderText="Select LOI date"
                    />
                  </div>
                </div>

                <div className={styles['form-grid-2']}>
                  <div className={styles['form-group']}>
                    <label>Expected Delivery <span className={styles['required-star']}>*</span></label>
                    <CustomDatePicker
                      selected={formData.expected_delivery_date}
                      onChange={(date) => handleDateChange('expected_delivery_date', date)}
                      placeholderText="Select date"
                    />
                  </div>
                  <div className={styles['form-group']}>
                    <label>Currency <span className={styles['required-star']}>*</span></label>
                    <CustomSelect
                      value={formData.currency_id ? (() => {
                        const c = currenciesData.find(curr => curr.id === formData.currency_id);
                        return c ? { value: c.id, label: `${c.name} (${c.symbol})` } : null;
                      })() : null}
                      onChange={(opt) => setFormData(prev => ({ ...prev, currency_id: opt ? opt.value : '' }))}
                      options={currenciesData.map(c => ({ value: c.id, label: `${c.name} (${c.symbol})` }))}
                      placeholder="Select Currency"
                      isLoading={fetchingDropdowns}
                    />
                  </div>
                </div>

                <div className={styles['form-group']}>
                  <label>Payment Terms</label>
                  <input
                    type="text"
                    name="payment_terms"
                    value={formData.payment_terms}
                    placeholder="e.g. Net 30, 50% Advance"
                    onChange={handleInputChange}
                  />
                </div>

                <div className={styles['form-grid-2']}>
                  <div className={styles['form-group']}>
                    <label>Internal Notes</label>
                    <textarea
                      name="internal_notes"
                      value={formData.internal_notes}
                      placeholder="Internal reference notes..."
                      onChange={handleInputChange}
                      rows="3"
                      className={styles['form-textarea']}
                    />
                  </div>
                  <div className={styles['form-group']}>
                    <label>Vendor Instructions</label>
                    <textarea
                      name="vendor_instructions"
                      value={formData.vendor_instructions}
                      placeholder="Instructions for the vendor..."
                      onChange={handleInputChange}
                      rows="3"
                      className={styles['form-textarea']}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Right Section: Upload */}
            <div className={[styles['form-card'], styles['upload-card']].join(' ')}>
              <div className={styles['card-header-po']}>
                <h3>Upload {labelBase} Document</h3>
              </div>
              <div className={styles['card-content']}>
                <div
                  className={`${styles['dropzone']} ${poDocument ? styles['has-file'] : ''}`}
                  onClick={() => !poDocument && fileInputRef.current.click()}
                  onDragOver={onDragOver}
                  onDrop={onDrop}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                    accept=".pdf,.jpg,.jpeg,.png"
                  />
                  {poDocument ? (
                    <div className={styles['file-preview-area']}>
                      {previewUrl ? (
                        <div className={styles['image-preview-container']}>
                          <img src={previewUrl} alt={`${labelBase} Preview`} className={styles['preview-image']} />
                          <div className={styles['preview-overlay']}>
                            <button className={styles['btn-remove-preview']} onClick={(e) => {
                              e.stopPropagation();
                              setPoDocument(null);
                              setPreviewUrl(null);
                            }}>
                              <Trash2 size={20} />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className={styles['file-selected-info']}>
                          <div className={styles['pdf-icon-wrapper']}>
                            <FileText size={40} className={styles['text-orange']} />
                          </div>
                          <div className={styles['file-details']}>
                            <p className={styles['file-name']}>{poDocument.name || `${labelBase} Document`}</p>
                            <p className={styles['file-meta']}>Document linked • Click to replace</p>
                          </div>
                          <button className={styles['btn-remove-file']} onClick={(e) => {
                            e.stopPropagation();
                            setPoDocument(null);
                          }}>
                            <X size={18} />
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className={styles['upload-icon']}>
                        <Upload size={32} />
                      </div>
                      <p>Drag & drop files here or <span className={styles['browse-text']}>browse</span></p>
                      <span className={styles['upload-hint']}>PDF, JPG up to 10MB</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Link Work Order Items */}
          <div className={[styles['form-card'], styles['wo-link-card']].join(' ')}>
            <div className={styles['card-header-po']}>
              <LinkIcon size={18} className={styles['text-orange']} />
              <h3>Link Work Order Items</h3>
            </div>
            <div className={styles['card-content']}>
              <div className={styles['wo-selection-group']}>
                <div className={styles['form-group']}>
                  <label>Select Work Orders <span className={styles['required-star']}>*</span></label>
                  <CustomSelect
                    isMulti={true}
                    options={workOrdersData.map(wo => ({ value: wo.id, label: `${wo.item_name} (#ITEM-${wo.id})` }))}
                    value={workOrdersData
                      .filter(wo => selectedWorkOrders.includes(wo.id))
                      .map(wo => ({ value: wo.id, label: `${wo.item_name} (#ITEM-${wo.id})` }))}
                    onChange={(selected) => {
                      setSelectedWorkOrders(selected ? selected.map(s => s.value) : []);
                    }}
                    placeholder="Search and select work order items..."
                    isClearable={true}
                    isLoading={fetchingWorkOrders}
                  />
                </div>
                <button type="button" className={styles['btn-load-items']} onClick={handleLoadItems} title="Load Selected Items">
                  <Check size={20} />
                </button>
              </div>
            </div>
          </div>

          {/* Purchase Order Items */}
          <div className={[styles['form-card'], styles['po-items-card']].join(' ')}>
            <div className={styles['card-header-po']}>
              <List size={18} className={styles['text-orange']} />
              <h3>{labelBase} Items</h3>
            </div>
            <div className={styles['card-content']}>
              {orderItems.length > 0 ? (
                <div className={styles['custom-table-wrapper']}>
                  <table className={styles['custom-table']}>
                    <thead>
                      <tr>
                        <th>Item Code</th>
                        <th>Item Name</th>
                        <th>Quantity</th>
                        <th>Unit</th>
                        <th>Rate</th>
                        <th>Tax</th>
                        <th>Amount</th>
                        <th className={styles['action-col']}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {orderItems.map(item => (
                        <tr key={item.id}>
                          <td className={styles['code-cell']}>#ITEM-{item.originalId || item.id}</td>
                          <td className={styles['name-cell']}>{item.name}</td>
                          <td>
                            <input
                              type="number"
                              className={styles['editable-input']}
                              value={item.qty}
                              onChange={(e) => handleItemChange(item.id, 'qty', parseFloat(e.target.value) || 0)}
                              min="0"
                            />
                          </td>
                          <td>{item.unit}</td>
                          <td>
                            <div className={styles['rate-input-wrapper']}>
                              <span className={styles['currency-symbol-rate']}>₹</span>
                              <input
                                type="number"
                                className={styles['editable-input']}
                                value={item.rate}
                                onChange={(e) => handleItemChange(item.id, 'rate', parseFloat(e.target.value) || 0)}
                                min="0"
                              />
                            </div>
                          </td>
                          <td style={{ minWidth: '150px' }}>
                            <CustomSelect
                              value={item.tax_id ? {
                                value: item.tax_id,
                                label: taxesData.find(t => t.id === item.tax_id)?.tax_name
                              } : null}
                              onChange={(opt) => handleItemChange(item.id, 'tax_id', opt ? opt.value : '')}
                              options={taxesData.map(t => ({ value: t.id, label: `${t.tax_name} (${t.tax_percentage}%)` }))}
                              placeholder="Select Tax"
                              isLoading={fetchingDropdowns}
                              isClearable={true}
                            />
                          </td>
                          <td className={styles['bold-cell']}>₹{item.amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td className={styles['action-col']}>
                            <button className={styles['delete-btn']} onClick={() => removeOrderItem(item.id)}>
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className={styles['table-footer']}>
                    <div className={styles['note-area']}>
                      <p className={styles['footer-info-text']}>
                        Tax is applied individually to each item above.
                      </p>
                    </div>
                    <div className={styles['total-summary']}>
                      <div className={styles['summary-row']}>
                        <span>Subtotal:</span>
                        <span className={styles['bold-cell']}>₹{totals.subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </div>
                      <div className={styles['summary-row']}>
                        <span>Total Tax:</span>
                        <span>+ ₹{totals.taxAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </div>
                      <div className={styles['summary-row']}>
                        <span>Round Off:</span>
                        <div className={styles['rate-input-wrapper']} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '120px' }}>
                          <span style={{ marginRight: '4px' }}>₹</span>
                          <input
                            type="number"
                            className={styles['editable-input']}
                            style={{ textAlign: 'right', padding: '4px' }}
                            value={formData.round_off}
                            onChange={(e) => setFormData(prev => ({ ...prev, round_off: e.target.value }))}
                            placeholder="0.00"
                            step="0.01"
                          />
                        </div>
                      </div>
                      <div className={[styles['summary-row'], styles['grand-total']].join(' ')}>
                        <span>Total Amount:</span>
                        <span className={styles['bold-cell']}>₹{totals.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className={styles['empty-items-state']}>
                  <p>No items added yet. Select work orders above to load items.</p>
                </div>
              )}
            </div>
          </div>
          <div className={styles['header-actions-btns']}>
            <button className={styles['btn-cancel']} onClick={() => navigate(pathPrefix)} disabled={isSubmitting}>Cancel</button>
            <button className={styles['btn-draft']} onClick={() => handleUpdate('draft')} disabled={isSubmitting}>
              {isSubmitting ? <Loader2 size={18} className={styles['spinner']} /> : <Save size={18} />}
              <span>Save Draft</span>
            </button>
            <button className={styles['btn-primary']} onClick={() => handleUpdate('pending')} disabled={isSubmitting}>
              {isSubmitting ? <Loader2 size={18} className={styles['spinner']} /> : <CheckCircle2 size={18} />}
              <span>{isSubmitting ? 'Updating...' : `Update ${labelBase}`}</span>
            </button>
          </div>
        </main>
      </div>

      <VendorDrawer
        isOpen={isAddVendorOpen}
        onClose={() => setIsAddVendorOpen(false)}
        mode="add"
      />
    </div>
  );
};

export default EditLOI;
